var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { decodeFixedPointType, getTxProof, allocateAmountsToVaults, storageKeyToNthInner, ensureHashEncoded, addHexPrefix, parseIssueRequest, newMonetaryAmount, newVaultId, newCurrencyId, } from "../utils";
import { currencyIdToMonetaryCurrency } from "../utils";
export class DefaultIssueAPI {
    constructor(api, btcNetwork, electrsAPI, wrappedCurrency, vaultsAPI, transactionAPI) {
        this.api = api;
        this.btcNetwork = btcNetwork;
        this.electrsAPI = electrsAPI;
        this.wrappedCurrency = wrappedCurrency;
        this.vaultsAPI = vaultsAPI;
        this.transactionAPI = transactionAPI;
    }
    getRequestLimits(vaults) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!vaults)
                vaults = yield this.vaultsAPI.getVaultsWithIssuableTokens();
            const vaultsArr = [...vaults.entries()].sort(
            // sort in descending order
            ([_id_1, amount_1], [_id_2, amount_2]) => amount_2.sub(amount_1).toBig().toNumber());
            if (vaultsArr.length === 0) {
                return {
                    singleVaultMaxIssuable: newMonetaryAmount(0, this.wrappedCurrency),
                    totalMaxIssuable: newMonetaryAmount(0, this.wrappedCurrency),
                };
            }
            const singleVaultMaxIssuable = vaultsArr[0][1];
            const totalMaxIssuable = vaultsArr.reduce((total, [_, vaultAvailable]) => {
                return total.add(vaultAvailable);
            }, newMonetaryAmount(0, this.wrappedCurrency));
            return { singleVaultMaxIssuable, totalMaxIssuable };
        });
    }
    request(amount, vaultAccountId, collateralCurrency, atomic = true, cachedVaults, griefingCollateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (vaultAccountId) {
                    if (!collateralCurrency) {
                        return Promise.reject(new Error("A collateral currency must be specified along with the vault account ID"));
                    }
                    // If a vault account id is defined, request to issue with that vault only.
                    // Initialize the `amountsPerVault` map with a single entry, the (vaultId, amount) pair
                    const collateralCurrencyId = newCurrencyId(this.api, collateralCurrency);
                    const vaultId = newVaultId(this.api, vaultAccountId.toString(), yield currencyIdToMonetaryCurrency(this.api, collateralCurrencyId), this.wrappedCurrency);
                    const amountsPerVault = new Map([
                        [vaultId, amount],
                    ]);
                    return this.requestAdvanced(amountsPerVault, atomic, griefingCollateralCurrency);
                }
                const availableVaults = cachedVaults || (yield this.vaultsAPI.getVaultsWithIssuableTokens());
                const amountsPerVault = allocateAmountsToVaults(availableVaults, amount);
                return this.requestAdvanced(amountsPerVault, atomic, griefingCollateralCurrency);
            }
            catch (e) {
                return Promise.reject(e);
            }
        });
    }
    buildRequestIssueExtrinsic(vaultId, amount, griefingCollateralCurrency) {
        // NOTE: If griefing collateral currency is not used, native currency is required as griefing collateral.
        const griefingCollateralCurrencyId = griefingCollateralCurrency
            ? newCurrencyId(this.api, griefingCollateralCurrency)
            : this.api.consts.currency.getNativeCurrencyId;
        return this.api.tx.issue.requestIssue(amount.toString(true), vaultId, griefingCollateralCurrencyId);
    }
    requestAdvanced(amountsPerVault, atomic, griefingCollateralCurrency) {
        const txs = Array.from(amountsPerVault.entries()).map(([vaultId, amount]) => this.buildRequestIssueExtrinsic(vaultId, amount, griefingCollateralCurrency));
        const batch = this.transactionAPI.buildBatchExtrinsic(txs, atomic);
        return { extrinsic: batch, event: this.api.events.issue.RequestIssue };
    }
    buildExecuteIssueExtrinsic(requestId, btcTxId) {
        return __awaiter(this, void 0, void 0, function* () {
            const parsedRequestId = ensureHashEncoded(this.api, requestId);
            const fullTxProof = yield getTxProof(this.electrsAPI, btcTxId);
            return this.api.tx.issue.executeIssue(parsedRequestId, fullTxProof);
        });
    }
    execute(requestId, btcTxId) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.buildExecuteIssueExtrinsic(requestId, btcTxId);
            return { extrinsic: tx, event: this.api.events.issue.ExecuteIssue };
        });
    }
    buildCancelIssueExtrinsic(requestId) {
        const parsedRequestId = this.api.createType("H256", addHexPrefix(requestId));
        return this.api.tx.issue.cancelIssue(parsedRequestId);
    }
    cancel(requestId) {
        const cancelIssueTx = this.buildCancelIssueExtrinsic(requestId);
        return { extrinsic: cancelIssueTx, event: this.api.events.issue.CancelIssue };
    }
    setIssuePeriod(blocks) {
        const period = this.api.createType("BlockNumber", blocks);
        const tx = this.api.tx.sudo.sudo(this.api.tx.issue.setIssuePeriod(period));
        return { extrinsic: tx };
    }
    getIssuePeriod() {
        return __awaiter(this, void 0, void 0, function* () {
            const blockNumber = yield this.api.query.issue.issuePeriod();
            return blockNumber.toNumber();
        });
    }
    list() {
        return __awaiter(this, void 0, void 0, function* () {
            const issueRequests = yield this.api.query.issue.issueRequests.entries();
            return yield Promise.all(issueRequests
                .filter(([_, req]) => req.isSome.valueOf())
                // can be unwrapped because the filter removes `None` values
                .map(([id, req]) => parseIssueRequest(this.api, this.vaultsAPI, req.unwrap(), this.btcNetwork, storageKeyToNthInner(id))));
        });
    }
    getFeesToPay(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const feePercentage = yield this.getFeeRate();
            return amount.mul(feePercentage);
        });
    }
    getDustValue() {
        return __awaiter(this, void 0, void 0, function* () {
            const dustValueSat = yield this.api.query.issue.issueBtcDustValue();
            return newMonetaryAmount(dustValueSat.toString(), this.wrappedCurrency);
        });
    }
    getFeeRate() {
        return __awaiter(this, void 0, void 0, function* () {
            const issueFee = yield this.api.query.fee.issueFee();
            return decodeFixedPointType(issueFee);
        });
    }
    getRequestById(issueId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getRequestsByIds([issueId]))[0];
        });
    }
    getRequestsByIds(issueIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const api = yield this.api.at(head);
            const issueRequestData = yield Promise.all(issueIds.map((issueId) => __awaiter(this, void 0, void 0, function* () {
                return new Promise((resolve, reject) => {
                    api.query.issue
                        .issueRequests(ensureHashEncoded(this.api, issueId))
                        .then((request) => resolve([request, issueId]))
                        .catch(reject);
                });
            })));
            // TODO: pass head to parseIssueRequest since it queries chain state
            return Promise.all(issueRequestData
                .filter(([option, _]) => option.isSome)
                .map(([issueRequest, issueId]) => parseIssueRequest(this.api, this.vaultsAPI, issueRequest.unwrap(), this.btcNetwork, issueId)));
        });
    }
    getVaultIssuableAmount(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.vaultsAPI.getIssuableTokensFromVault(vaultAccountId, collateralCurrency);
        });
    }
}
//# sourceMappingURL=issue.js.map